import {SyntheticEvent, useEffect, useRef} from "react";
import {Eye, EyeOff, LoaderCircle, XIcon} from "lucide-react";
import CharacterDetails from "@/characters/admin/details/CharacterDetails.tsx";
import {Button} from "@/components/ui/button.tsx";
import {cn} from "@/lib/utils.ts";
import {useModal} from "@/modal/ModalHooks.ts";
import SpellDetails from "@/spells/SpellDetails.tsx";

interface OverlayModalProps {
    readOnly?: boolean;
}

export default function OverlayModal({readOnly = false}: OverlayModalProps) {
    const ref = useRef<HTMLDialogElement>(null);
    const {
        content,
        hide,
        visibleToPlayer,
        setVisibleToPlayer,
        loading,
        updatingVisibility,
        error,
    } = useModal();
    const hasBody = Boolean(content || loading || error);
    const openModal = hasBody && (!readOnly || visibleToPlayer);
    const title = content?.type === "SPELL"
        ? content.spell.name
        : content?.type === "CHARACTER" ? content.character.name : "Präsentation";

    useEffect(() => {
        const dialog = ref.current;
        if (!dialog) return;
        if (openModal && !dialog.open) dialog.showModal();
        if (!openModal && dialog.open) dialog.close();
        return () => {
            if (dialog.open) dialog.close();
        };
    }, [openModal]);

    const handleCancel = (event: SyntheticEvent<HTMLDialogElement>) => {
        if (readOnly) {
            event.preventDefault();
        } else {
            hide();
        }
    };

    if (!openModal) return null;

    return (
        <dialog
            ref={ref}
            aria-label={title}
            onCancel={handleCancel}
            style={{left: "50%", top: "50%", transform: "translate(-50%, -50%)", width: "80%"}}
            className="max-h-[90vh] bg-transparent backdrop:backdrop-brightness-50 focus:outline-hidden"
        >
            <div className="max-h-[90vh] overflow-y-auto rounded-md border border-black bg-stone-800 text-white shadow-sm">
                {!readOnly && (
                    <div className="sticky top-0 z-10 flex items-center justify-between bg-stone-800 p-1">
                        <Button
                            type="button"
                            size="sm"
                            disabled={!content || loading || updatingVisibility}
                            onClick={() => setVisibleToPlayer(!visibleToPlayer)}
                            className={cn(
                                "text-white",
                                visibleToPlayer ? "bg-red-900 hover:bg-red-800" : "bg-sky-950 hover:bg-sky-900",
                            )}
                        >
                            {updatingVisibility && <LoaderCircle className="animate-spin"/>}
                            {!updatingVisibility && visibleToPlayer && <><EyeOff/>Verbergen</>}
                            {!updatingVisibility && !visibleToPlayer && <><Eye/>Anzeigen</>}
                        </Button>
                        <Button
                            type="button"
                            size="icon-sm"
                            variant="secondary"
                            aria-label="Dialog schließen"
                            className="bg-sky-950 hover:bg-sky-900"
                            onClick={hide}
                        >
                            <XIcon/>
                        </Button>
                    </div>
                )}
                {loading && (
                    <div className="flex min-h-48 items-center justify-center gap-2" role="status">
                        <LoaderCircle className="animate-spin"/> Inhalt wird geladen…
                    </div>
                )}
                {!loading && error && <p className="p-6 text-center text-red-200" role="alert">{error}</p>}
                {!loading && content?.type === "SPELL" && <SpellDetails spell={content.spell}/>}
                {!loading && content?.type === "CHARACTER" && <CharacterDetails character={content.character}/>}
            </div>
        </dialog>
    );
}
